/**
 * FocusPanel — the one column of controls beside the preview (spec §3).
 *
 * The problem it solves: the Studio's inspector is 4 element tabs × 5
 * accordion sections of exact numbers. That is the right surface for the
 * person who knows what `letterSpacing: "0.5px"` does to an Arabic punch
 * line, and the wrong one for the person opening the app for the first
 * time. So this panel shows at most ~7 controls per element — the ones that
 * answer "make it bigger / move it down / change the colour" — and puts the
 * WHOLE existing inspector one click away behind **More options**, mounted
 * INSIDE this panel (`showElementTabs={false}`), scrolling internally.
 *
 * Two rules hold the design together:
 *
 * 1. **Nothing is extracted, nothing is forked.** Every essentials row
 *    writes an `EditPatch` through `inspector.onPreview` / `inspector.onCommit`
 *    — the same patch, the same validator, the same journal the inspector
 *    and the chat already write (ADR-0077). This panel owns no state that
 *    describes the design; it reads `inspector.layout` / `inspector.brand`.
 * 2. **The two views are exclusive.** Essentials OR the inspector, never
 *    both, so no field renders twice and there is no question about which
 *    copy of "Font size" is the real one. "More options" is not persisted:
 *    it collapses whenever the selected element changes.
 *
 * This file is the FRAME — tabs, scope line, ⋯ menu, More-options switch,
 * Undo footer. The five essentials views live in `tabs/`.
 *
 * Hosts: the Brand Setup wizard's Review › Adjust step and the Studio. They
 * differ only in `host` (scope wording, the transcript list, the style
 * menu) — the controls themselves are identical, because the operator is
 * learning ONE editor.
 */
import {
  Captions,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  MoreHorizontal,
  MousePointerClick,
  Sun,
  Type,
  Undo2,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { EditPatch } from "../../../../src/lib/clipEditState.ts";
import type { SerializableCue } from "../../../../src/remotion/types.ts";
import { NO_SELECTION, type StudioElementId, type StudioSelection } from "../../lib/studioTypes.ts";
import { StudioInspector, captionResetPatch, type StudioInspectorProps } from "../StudioInspector.tsx";
import { ctaSmallStyle, iconBtnStyle } from "../styles.ts";
import { CaptionTab } from "./tabs/CaptionTab.tsx";
import { ExtraTextTab } from "./tabs/ExtraTextTab.tsx";
import { FootageTab } from "./tabs/FootageTab.tsx";
import { LogoTab } from "./tabs/LogoTab.tsx";
import { SpeakerTab } from "./tabs/SpeakerTab.tsx";

// One definition, two surfaces: the placement maths belongs to Brand Setup
// (where the steps write the same numbers), and this is the public spelling
// for anything that already imports it from the panel.
export { lowerThirdBottomPx } from "../../lib/brandSetup/geometry.ts";

export type FocusTab = "caption" | "logo" | "speaker" | "video" | "overlays";

export interface FocusPanelProps {
  host: "brand-setup" | "studio";
  /** The SAME object the host would have passed to `<StudioInspector>` —
   *  which is exactly what "More options" mounts. */
  inspector: StudioInspectorProps;
  selection: StudioSelection;
  onSelect: (s: StudioSelection) => void;
  /** Every cue, for the Studio's transcript list (seek + select). */
  cues: SerializableCue[];
  onSelectCue: (i: number) => void;
  /** What ⌘Z would undo, in the operator's words — null = nothing. */
  undoLabel: string | null;
  onUndo: () => void;
  /** Studio only: lift this clip's design onto the channel. */
  onSavePromote?: () => void;
  canPromote?: boolean;
  promoting?: boolean;
  width?: number | string;
}

const TABS: Array<{ id: FocusTab; label: string; icon: React.ReactNode }> = [
  { id: "caption", label: "Captions", icon: <Captions size={14} /> },
  { id: "logo", label: "Logo", icon: <ImageIcon size={14} /> },
  { id: "speaker", label: "Speaker", icon: <User size={14} /> },
  // id stays "video" — the canvas overlay and the chat's parseSelection key
  // off it; only the label is the operator's.
  { id: "video", label: "Footage", icon: <Sun size={14} /> },
  { id: "overlays", label: "Extra text", icon: <Type size={14} /> },
];

/** How the ⋯ menu names each element in "Reset …". */
const RESET_NOUN: Record<StudioElementId, string> = {
  caption: "captions",
  logo: "logo",
  speaker: "speaker",
  video: "footage",
};

/** True when the keyboard focus is a text-entry control (native undo wins). */
function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable;
}

/**
 * The patch that restores one element to the clip's pre-edit design — the
 * inspector's own per-panel Reset, so both surfaces restore exactly the same
 * thing (captions share the literal function).
 */
function resetPatch(el: StudioElementId, baseline: StudioInspectorProps["baseline"]): EditPatch {
  switch (el) {
    case "caption":
      return captionResetPatch(baseline);
    case "logo":
      return { layout: { logo: baseline.logo } };
    case "speaker":
      // `baseline.speakerCard` is PLACEMENT only (x/y/scale) — the card's
      // typography and timing have no baseline because absent means "the
      // still generator's own default". Restating the three unsettable keys
      // as null is what actually restores that default; without them a
      // Reset would leave this very tab's Name size / Shows for / Role line
      // exactly where the operator dragged them.
      return {
        layout: {
          speakerCard: { nameFontPx: null, durationSec: null, descriptionWords: null, ...baseline.speakerCard },
        },
      };
    case "video":
      return { layout: { video: baseline.video } };
  }
}

export function FocusPanel(p: FocusPanelProps): React.JSX.Element {
  const {
    host,
    inspector,
    selection,
    onSelect,
    cues,
    onSelectCue,
    undoLabel,
    onUndo,
    onSavePromote,
    canPromote = false,
    promoting = false,
    width = "clamp(260px, 22vw, 340px)",
  } = p;
  const { layout, onCommit } = inspector;
  /**
   * `undefined` is NOT `[]` here, and the difference is destructive: every
   * overlay write replaces the whole list, so a host that doesn't track them
   * would have "Add a line" commit a replace that drops the clip's real
   * CTA. `undefined` = "this host doesn't carry them" and the tab says so
   * (the same gate `StudioInspector`'s Overlays section keeps).
   */
  const overlays = layout.overlays;

  const [tab, setTab] = useState<FocusTab | null>(selection.el);
  const [more, setMore] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const menuRef = useRef<HTMLDivElement | null>(null);

  // A canvas click is the other way into this panel: the tab follows the
  // selection. "Extra text" has no element, so it survives a cleared
  // selection instead of snapping back to the empty state.
  useEffect(() => {
    setTab((t) => (selection.el ? selection.el : t === "overlays" ? "overlays" : null));
  }, [selection.el]);

  // "More options" is NOT persisted — it collapses on every element change,
  // so the next element opens on its essentials (spec §3).
  useEffect(() => {
    setMore(false);
  }, [selection.el, tab]);

  // ⌘Z / Ctrl+Z is the panel's, in the CAPTURE phase: a host that also
  // listens on window would otherwise undo twice for one keystroke.
  useEffect(() => {
    const h = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.key.toLowerCase() !== "z") return;
      if (isTypingTarget(e.target)) return; // text fields keep native undo
      e.preventDefault();
      e.stopPropagation();
      onUndo();
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [onUndo]);

  // Escape dismisses whatever is layered on top, innermost first.
  useEffect(() => {
    if (!menuOpen && !presetsOpen) return;
    const h = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (menuOpen) setMenuOpen(false);
      else setPresetsOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [menuOpen, presetsOpen]);

  // Click anywhere else closes the ⋯ menu.
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  const pickTab = (id: FocusTab): void => {
    setTab(id);
    if (id === "overlays") onSelect(NO_SELECTION);
    else onSelect({ el: id, cueIdx: id === "caption" ? selection.cueIdx : null });
  };

  const scope =
    host === "brand-setup"
      ? "Your brand's look"
      : selection.el === "caption" && selection.cueIdx != null
        // Honest scope (2026-09-17): only the TEXT is per-caption; Size /
        // Lines / Position below it are clip-wide, and the line says so.
        ? "Text: this caption · Size, lines, position: all captions"
        : "This clip only · Save as my style to apply everywhere";

  const essentials =
    tab === "caption" ? (
      <CaptionTab
        host={host}
        inspector={inspector}
        selection={selection}
        cues={cues}
        onSelectCue={onSelectCue}
        query={query}
        onQueryChange={setQuery}
        onOpenMore={() => setMore(true)}
      />
    ) : tab === "logo" ? (
      <LogoTab inspector={inspector} />
    ) : tab === "speaker" ? (
      <SpeakerTab inspector={inspector} />
    ) : tab === "video" ? (
      <FootageTab inspector={inspector} />
    ) : overlays === undefined ? (
      <span style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>
        This clip's extra text isn't loaded here.
      </span>
    ) : (
      <ExtraTextTab inspector={inspector} overlays={overlays} />
    );

  const emptyState = (
    <div style={{ display: "grid", gap: 10, justifyItems: "center", textAlign: "center", padding: "16px 4px" }}>
      <span
        style={{
          width: 36, height: 36, borderRadius: 11, background: "rgba(255,213,0,.1)",
          display: "grid", placeItems: "center", color: "var(--ql-yellow)",
        }}
      >
        <MousePointerClick size={17} />
      </span>
      <span style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.6, maxWidth: 250 }}>
        {/* "or just ask" is the chat, and Brand Setup has none. */}
        {host === "studio"
          ? "Click anything on the video to change it — or just ask."
          : "Click anything on the video to change it."}
      </span>
      {host === "studio" && canPromote && onSavePromote && (
        <button
          type="button"
          onClick={onSavePromote}
          disabled={promoting}
          style={{
            padding: "8px 14px", borderRadius: 10, cursor: promoting ? "wait" : "pointer", fontFamily: "inherit",
            fontSize: 12, fontWeight: 700,
            border: "1px solid rgba(255,213,0,.35)", background: "rgba(255,213,0,.06)", color: "var(--ql-yellow)",
          }}
        >
          {promoting ? "Saving…" : "Save as my style"}
        </button>
      )}
    </div>
  );

  // ---------------------------------------------------------------- render

  return (
    <div
      style={{
        position: "relative",
        width,
        flexShrink: 0,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 16,
        border: "1px solid var(--ql-border)",
        background: "rgba(255,255,255,.02)",
        boxSizing: "border-box",
      }}
    >
      {/* ---- header: tabs · scope · ⋯ ---- */}
      <div style={{ padding: 10, display: "grid", gap: 8, borderBottom: "1px solid var(--ql-border)" }}>
        <div style={{ display: "flex", gap: 3, padding: 3, borderRadius: 11, background: "rgba(255,255,255,.03)" }}>
          {TABS.map((t) => {
            const active = tab === t.id;
            // "Extra text" is dimmed when there is nothing there (or when
            // this host doesn't carry the list) — never disabled: dimming
            // says "nothing here yet", disabling would hide the only way to
            // add the first line.
            const dim = t.id === "overlays" && (overlays === undefined || overlays.length === 0);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => pickTab(t.id)}
                aria-pressed={active}
                title={t.label}
                data-tour={t.id === "caption" ? "studio-captions-tab" : undefined}
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "grid",
                  justifyItems: "center",
                  gap: 3,
                  padding: "6px 2px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 9.5,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  border: "1px solid " + (active ? "var(--ql-yellow)" : "transparent"),
                  background: active ? "rgba(255,213,0,.10)" : "transparent",
                  color: active ? "var(--ql-fg)" : dim ? "var(--ql-fg-faint)" : "var(--ql-fg-muted)",
                  opacity: dim && !active ? 0.65 : 1,
                }}
              >
                <span style={{ display: "inline-flex", color: active ? "var(--ql-yellow)" : "inherit" }}>{t.icon}</span>
                {t.label}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 11, color: "var(--ql-fg-subtle)", lineHeight: 1.45, minWidth: 0 }}>{scope}</span>
          <div ref={menuRef} style={{ position: "relative", marginLeft: "auto", flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              title="More actions"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={{ ...iconBtnStyle, width: 28, height: 28 }}
            >
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: "absolute", top: "calc(100% + 5px)", insetInlineEnd: 0, zIndex: 20,
                  minWidth: 176, display: "grid", gap: 2, padding: 5, borderRadius: 11,
                  border: "1px solid var(--ql-border-strong)", background: "var(--ql-bg)",
                  boxShadow: "0 18px 50px rgba(0,0,0,.55)",
                }}
              >
                {/* Reset is per-ELEMENT; "Extra text" has no baseline (absent
                    = none), and wiping every line is a removal, not a reset. */}
                {tab && tab !== "overlays" && (
                  <MenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onCommit(resetPatch(tab, inspector.baseline), `Reset ${RESET_NOUN[tab]} to this clip's design`);
                    }}
                  >
                    Reset {RESET_NOUN[tab]}
                  </MenuItem>
                )}
                {host === "studio" && (
                  <MenuItem onClick={() => { setMenuOpen(false); setPresetsOpen(true); }}>Style presets</MenuItem>
                )}
                {host === "studio" && onSavePromote && (
                  <MenuItem
                    disabled={!canPromote || promoting}
                    onClick={() => { setMenuOpen(false); onSavePromote(); }}
                  >
                    {promoting ? "Saving…" : "Save as my style"}
                  </MenuItem>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- body: essentials OR the whole inspector — never both ---- */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 12, display: "grid", gap: 13, alignContent: "start" }}>
        {tab === null
          ? emptyState
          : more
            ? <StudioInspector {...inspector} selection={selection} showElementTabs={false} />
            : essentials}
      </div>

      {/* ---- More options toggle (fixed, so it never scrolls away) ---- */}
      {tab !== null && (
        <button
          type="button"
          onClick={() => setMore((v) => !v)}
          aria-expanded={more}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "9px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700,
            border: "none", borderTop: "1px solid var(--ql-border)", background: "transparent",
            color: "var(--ql-fg-muted)",
          }}
        >
          {more ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {more ? "Fewer options" : "More options"}
        </button>
      )}

      {/* ---- footer: Undo, and only Undo — and only when there IS one.
           A button whose only possible answer is "no" is not a control
           (spec §1.8, QA 2026-09-16 m8). ---- */}
      {undoLabel && (
        <div style={{ padding: 8, borderTop: "1px solid var(--ql-border)" }}>
          <button
            type="button"
            onClick={onUndo}
            title={`Undo “${undoLabel}” (⌘Z)`}
            style={{
              width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: "9px 10px", borderRadius: 10, fontFamily: "inherit", fontSize: 12, fontWeight: 700,
              cursor: "pointer",
              border: "1px solid var(--ql-border-strong)",
              background: "rgba(255,255,255,.04)",
              color: "var(--ql-fg)",
              minWidth: 0,
            }}
          >
            <Undo2 size={13} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              Undo {undoLabel}
            </span>
          </button>
        </div>
      )}

      {/* ---- Style presets popover: the inspector's OWN clip panel ---- */}
      {presetsOpen && (
        <div
          style={{
            position: "absolute", inset: 0, zIndex: 30, display: "flex", flexDirection: "column",
            background: "var(--ql-bg)", borderRadius: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--ql-border)" }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>Style presets</span>
            <button
              type="button"
              onClick={() => setPresetsOpen(false)}
              style={{ ...ctaSmallStyle, marginInlineStart: "auto", padding: "6px 12px", fontSize: 11.5 }}
            >
              Done
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 12 }}>
            <StudioInspector {...inspector} showElementTabs={false} selection={NO_SELECTION} />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function MenuItem({ children, onClick, disabled = false }: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: "start", padding: "8px 10px", borderRadius: 8, border: "none", background: "transparent",
        cursor: disabled ? "default" : "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
        color: disabled ? "var(--ql-fg-faint)" : "var(--ql-fg)",
      }}
    >
      {children}
    </button>
  );
}
